import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Play, RotateCcw, Download, Sparkles, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import type { ChatTestBatch } from '@/core/models';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { downloadFile } from '@/core/services/file-download';

export const Route = createFileRoute('/battery-test')({
  head: () => ({
    meta: [{ title: 'Batterie Test · Replya Internal Backoffice' }],
  }),
  component: BatteryTestPage,
});

const DEFAULT_QUESTIONS = [
  'Quali prodotti consigli per un bagno moderno?',
  "Che differenza c'è tra gres porcellanato e ceramica?",
  'Hai prodotti adatti per esterni?',
];

function exportBatteryCsv(tenantName: string, results: ChatTestBatch['results']) {
  const rows = [
    ['Domanda', 'Successo', 'Risposta', 'Errore', 'Tempo (ms)', 'Prodotti suggeriti'],
    ...results.map((r) => [
      r.question,
      r.success ? 'Sì' : 'No',
      r.reply ?? '',
      r.error ?? '',
      String(r.responseTime ?? ''),
      r.products.map((p) => p.name).join('; '),
    ]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadFile(csv, `battery-test-${tenantName}-${Date.now()}.csv`, 'text/csv');
}

function BatteryTestPage() {
  const { selectedTenant } = useAppState();
  const [questionsText, setQuestionsText] = useState(DEFAULT_QUESTIONS.join('\n'));
  const [progressResults, setProgressResults] = useState<ChatTestBatch['results']>([]);
  const [progress, setProgress] = useState(0);
  const [evaluateWithAI, setEvaluateWithAI] = useState(false);

  const questions = questionsText
    .split('\n')
    .map((q) => q.trim())
    .filter(Boolean);

  const mutation = useMutation({
    mutationFn: async () => {
      setProgressResults([]);
      setProgress(0);
      if (evaluateWithAI) {
        return apiService.runEvaluatedBatteryTest(selectedTenant!.tenantId, questions, true);
      }
      return apiService.runBatteryTest(selectedTenant!.tenantId, questions, {
        onProgress: (_, index) => setProgress(Math.round(((index + 1) / questions.length) * 100)),
      });
    },
    onSuccess: (data) => {
      setProgressResults(data.results);
      setProgress(100);
    },
  });

  const reset = () => {
    setQuestionsText(DEFAULT_QUESTIONS.join('\n'));
    setProgressResults([]);
    setProgress(0);
    mutation.reset();
  };

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  const data = mutation.data;
  const results = progressResults;
  const isRunning = mutation.isPending;

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader title="Batterie Test" subtitle={`Esegui una batteria di domande su ${selectedTenant.name}.`}>
        {data && (
          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => exportBatteryCsv(selectedTenant.name, data.results)}>
            <Download className="mr-2 h-4 w-4" />
            Esporta CSV
          </Button>
        )}
      </PageHeader>

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle>Domande</CardTitle>
          <CardDescription>Inserisci una domanda per riga</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={questionsText}
            onChange={(e) => setQuestionsText(e.target.value)}
            rows={8}
            disabled={isRunning}
            className="rounded-xl bg-surface-2 border-border"
          />
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="eval-ai"
                checked={evaluateWithAI}
                onCheckedChange={setEvaluateWithAI}
                disabled={isRunning}
              />
              <Label htmlFor="eval-ai" className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-brand" />
                Valuta con AI
              </Label>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={reset} disabled={isRunning} className="rounded-xl">
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={isRunning || questions.length === 0}
                className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Esecuzione...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Avvia test
                  </>
                )}
              </Button>
            </div>
          </div>
          {isRunning && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2 rounded-full" />
              <p className="text-xs text-muted-foreground text-right">{progress}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {(data || results.length > 0) && (
        <Card className="rounded-2xl border bg-surface">
          <CardHeader>
            <CardTitle>Risultati</CardTitle>
            <CardDescription>
              {data
                ? `${data.successful} successi, ${data.failed} fallimenti, tempo medio ${data.averageResponseTime}ms`
                : `${results.length} di ${questions.length} completati`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {results.map((result, i) => (
              <div key={i} className="rounded-xl border border-border bg-surface-2 p-4">
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={result.success ? 'default' : 'destructive'} className="text-xs">
                        {result.success ? 'OK' : 'KO'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{result.responseTime}ms</span>
                      {'averageScore' in result && typeof result.averageScore === 'number' && (
                        <span className="text-xs text-brand font-medium">Score: {result.averageScore}</span>
                      )}
                    </div>
                    <p className="mt-1 font-medium text-sm">{result.question}</p>
                    {result.reply && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{result.reply}</p>
                    )}
                    {result.error && (
                      <p className="mt-2 text-sm text-destructive">{result.error}</p>
                    )}
                    {result.products.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {result.products.map((p) => (
                          <Badge key={p.id} variant="secondary" className="text-xs">
                            {p.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
